import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import { Contract } from '@ethersproject/contracts'
import MainWrapper from 'layouts/MainWrapper'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { useMarketplaceContext } from 'utils/marketplace/useMarketplaceContext'
import { useWeb3ProviderAndContract } from 'hooks/useWeb3ProviderAndContract'
import { ERC20_MIN_ABI, MARKETPLACE_ABI } from 'utils/marketplace/abi'
import { decodeMarketplaceError } from 'utils/marketplace/errorMessages'
import TxConfirmation from 'components/TxConfirmation'
import ExpiryPicker from './components/ExpiryPicker'
import useToast from 'hooks/useToast'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'

const Select = styled.select`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const FormColumn = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
`

const Section = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
`

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

/**
 * The Wanted form: a standing buy offer. The MPRO never leaves the buyer's
 * wallet — the two transactions here are an allowance and a recorded
 * intention, the exact mirror of a seller's approval + listing.
 */
const NewOfferForm: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const { account, provider } = useWeb3React()
  const ctx = useMarketplaceContext()
  const ensureChainForTx = useSwitchChainForContractTx()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  const { toastError } = useToast()

  const [nft, setNft] = useState('')
  const [unitPrice, setUnitPrice] = useState('')
  const [quantity, setQuantity] = useState('1')
  /** Local datetime string; empty = never expires. */
  const [expiry, setExpiry] = useState('')
  const [busy, setBusy] = useState<'approve' | 'offer' | null>(null)
  const [allowance, setAllowance] = useState<bigint | null>(null)
  const [txHash, setTxHash] = useState<string | null>(null)
  const [createdOfferId, setCreatedOfferId] = useState<string | null>(null)

  const totalWei = useMemo(() => {
    try {
      const unit = ethers.utils.parseEther(unitPrice || '0')
      return unit.mul(ethers.BigNumber.from(quantity || '0')).toBigInt()
    } catch {
      return 0n
    }
  }, [unitPrice, quantity])

  const expiryTs = useMemo(() => {
    if (!expiry) return 0n
    const ts = Math.floor(new Date(expiry).getTime() / 1000)
    return Number.isFinite(ts) && ts > 0 ? BigInt(ts) : 0n
  }, [expiry])

  // The contract's rule, enforced client-side too so the wallet prompt never
  // opens for a transaction that must revert. The contract stays the
  // authority — this is a courtesy, not the check.
  const expiryTooSoon = useMemo(() => expiryTs !== 0n && Number(expiryTs) * 1000 < Date.now() + 3600_000, [expiryTs])

  const refreshAllowance = useCallback(async () => {
    if (!account || !ctx.mproAddress || !ctx.marketplaceAddress) {
      setAllowance(null)
      return
    }
    try {
      const rpc = getSimpleRpcProviderByChainId(ctx.chainId)
      const mpro = new Contract(
        ctx.mproAddress,
        ERC20_MIN_ABI as unknown as ethers.ContractInterface,
        rpc as ethers.providers.Provider
      )
      const a: ethers.BigNumber = await mpro.allowance(account, ctx.marketplaceAddress)
      setAllowance(a.toBigInt())
    } catch {
      setAllowance(null)
    }
  }, [account, ctx.mproAddress, ctx.marketplaceAddress, ctx.chainId])

  useEffect(() => {
    void refreshAllowance()
  }, [refreshAllowance])

  const needsApproval = allowance !== null && totalWei > 0n && allowance < totalWei

  const onApprove = useCallback(async () => {
    if (!provider || !account || !ctx.mproAddress || !ctx.marketplaceAddress) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('approve')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const mpro = new Contract(ctx.mproAddress, ERC20_MIN_ABI as unknown as ethers.ContractInterface, signer)
      const tx = await mpro.approve(ctx.marketplaceAddress, totalWei)
      await tx.wait()
      await refreshAllowance()
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [
    provider,
    account,
    ctx.mproAddress,
    ctx.marketplaceAddress,
    ctx.chainId,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    totalWei,
    refreshAllowance,
    toastError,
    t
  ])

  const onMakeOffer = useCallback(async () => {
    if (!provider || !account || !ctx.marketplaceAddress || !nft) return
    if (totalWei === 0n || expiryTooSoon) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('offer')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const market = new Contract(
        ctx.marketplaceAddress,
        MARKETPLACE_ABI as unknown as ethers.ContractInterface,
        signer
      )
      const unit = ethers.utils.parseEther(unitPrice)
      const tx = await market.makeOffer(nft, unit, quantity, expiryTs)
      setTxHash(tx.hash)
      const receipt = await tx.wait()
      // Read the id from the OfferMade event for the success link.
      try {
        const made = receipt.logs
          .map((l: ethers.providers.Log) => {
            try {
              return market.interface.parseLog(l)
            } catch {
              return null
            }
          })
          .find((p: { name: string } | null) => p?.name === 'OfferMade')
        if (made) setCreatedOfferId(made.args.offerId.toString())
      } catch {
        /* the offer exists either way */
      }
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
      setTxHash(null)
    } finally {
      setBusy(null)
    }
  }, [
    provider,
    account,
    ctx.marketplaceAddress,
    ctx.chainId,
    nft,
    unitPrice,
    quantity,
    totalWei,
    expiryTs,
    expiryTooSoon,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    toastError,
    t
  ])

  const collectionLabel = ctx.collections.find((c) => c.nft === nft)?.label ?? ''

  return (
    <MainWrapper>
      <Flex fullWidth flexDirection="column" mt="152px" mb="120px">
        <FormColumn>
          <Text variant={TextVariant.H4}>{t('marketplace.wantedFormTitle')}</Text>

          {txHash ? (
            <Section>
              <TxConfirmation hash={txHash} chainId={ctx.chainId} title={t('marketplace.offerMadeTitle')} />
              <Flex gap="10px" flexWrap="wrap">
                {createdOfferId ? (
                  <Button size="sm" onClick={() => navigate(`/marketplace/offers/${createdOfferId}`)}>
                    {t('marketplace.viewOffer')}
                  </Button>
                ) : null}
                <Button size="sm" variant="secondary" onClick={() => navigate('/marketplace?type=wanted')}>
                  {t('marketplace.backToMarketplace')}
                </Button>
              </Flex>
            </Section>
          ) : (
            <>
              <Section>
                <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('marketplace.whichCollection')}</Text>
                <Select value={nft} onChange={(e) => setNft(e.target.value)}>
                  <option value="">{t('marketplace.chooseCollectionPlaceholder')}</option>
                  {ctx.collections.map((c) => (
                    <option key={c.nft} value={c.nft}>
                      {c.label}
                    </option>
                  ))}
                </Select>

                <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('marketplace.offerQuantity')}</Text>
                <Input type="number" min="1" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />

                <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('marketplace.offerUnitPrice')}</Text>
                <Input
                  inputMode="decimal"
                  placeholder={t('marketplace.pricePlaceholder')}
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                />

                <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('marketplace.offerExpiry')}</Text>
                <ExpiryPicker value={expiry} onChange={setExpiry} />
                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                  {t('marketplace.offerExpiryHint')}
                </Text>
                {expiryTooSoon && (
                  <Text variant={TextVariant.BODY_SMALL} color={colors.red}>
                    {t('marketplace.expiryTooSoon')}
                  </Text>
                )}
              </Section>

              {/*
                Adverse selection, said before the wallet opens: sellers will
                claim first and sell their emptiest licence into this bid. Not
                fixable on chain, and not to be fixed — but owed to the bidder
                as a plain sentence.
              */}
              <Section>
                <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                  {t('marketplace.offerAdverseSelection')}
                </Text>
                <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                  {t('marketplace.offerNoEscrow', {
                    total: ethers.utils.formatEther(totalWei)
                  })}
                </Text>
              </Section>

              <Section>
                {needsApproval ? (
                  <Button disabled={busy !== null || !nft || totalWei === 0n} onClick={() => void onApprove()}>
                    {busy === 'approve'
                      ? t('marketplace.approving')
                      : t('marketplace.approveMpro', {
                          amount: ethers.utils.formatEther(totalWei)
                        })}
                  </Button>
                ) : (
                  <Button
                    disabled={busy !== null || !nft || totalWei === 0n || expiryTooSoon || allowance === null}
                    onClick={() => void onMakeOffer()}
                  >
                    {busy === 'offer'
                      ? t('marketplace.offering')
                      : t('marketplace.placeOffer', { label: collectionLabel })}
                  </Button>
                )}
              </Section>
            </>
          )}
        </FormColumn>
      </Flex>
    </MainWrapper>
  )
}

export default NewOfferForm
